import type { BookingsFilters, CallsFilters, ReservationsFilters } from '@hostesa/api-client';
import { requireSession, useAuth } from '@hostesa/auth-client';
import { useFlag } from '@hostesa/feature-flags';
import { LoadingState, Toaster } from '@hostesa/ui';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { getAuthSnapshot } from './lib/auth-bridge';
import { useHotelContext } from './lib/hotel-context';
import type { RouterContext } from './lib/router-context';
import { firstAccessibleHotelPath } from './lib/slug-resolution';
import { AnalyticsPage } from './routes/analytics/analytics-page';
import { BookingsPage } from './routes/bookings/bookings-page';
import {
  CallDetailPage,
  CallsPage,
  ConversationsPage,
  LiveCallsPage,
} from './routes/calls/calls-page';
import { HotelProfilePage } from './routes/hotel/hotel-profile-page';
import { HotelSetupPage } from './routes/hotel-setup';
import { IntegrationsPage } from './routes/integrations/integrations-page';
import { KnowledgePage } from './routes/knowledge/knowledge-page';
import { LoginPage } from './routes/login/login-page';
import { OnboardingReviewPage } from './routes/onboarding/onboarding-page';
import { TasksPage, TasksRoutingPage } from './routes/operations/operations-page';
import { ReceptionPage } from './routes/reception/reception-page';
import { ReservationsPage } from './routes/reservations/reservations-page';
import { HandoffRulesPage, ScriptsPage } from './routes/scripts/scripts-page';
import { SettingsPage } from './routes/settings/settings-page';
import { NotFoundPage, OnboardingPage } from './routes/standalone-pages';
import { TeamPage } from './routes/team/team-page';
import { UsagePage } from './routes/usage/usage-page';
import { HotelGuard } from './shell/hotel-guard';
import { ImpersonationBanner } from './shell/impersonation-banner';
import { PermissionRoute } from './shell/permission-route';

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <ImpersonationBanner />
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

// ---------------------------------------------------------------------------
// Public routes
// ---------------------------------------------------------------------------

// Bare-domain landing: nobody types app.hostesa.ai expecting a 404. Guests
// without a session go to /login; a live session reuses the /__handoff landing
// logic (first accessible hotel's reception, /onboarding fallback).
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    const { token } = getAuthSnapshot();
    if (!token) throw redirect({ to: '/login' });
    throw redirect({ to: '/__handoff' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === 'string' ? (search.next as string) : undefined,
  }),
  component: LoginPage,
});

const sessionGate = ({ location }: { location: { href: string } }) => {
  const { token } = getAuthSnapshot();
  const decision = requireSession(token, location.href);
  if ('redirectTo' in decision) throw redirect({ to: decision.redirectTo });
};

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/onboarding',
  beforeLoad: sessionGate,
  component: OnboardingPage,
});

/**
 * Operator impersonation landing route. The impersonation token is consumed
 * from the URL fragment in `main.tsx` BEFORE this route renders (so the first
 * `/auth/me` already carries it). By the time we get here the session is
 * established, so this route's only job is to land the operator on the
 * post-login destination — the first accessible hotel's reception — reusing the
 * exact same landing logic as login. Falls back to `/onboarding` when no hotel
 * is accessible.
 */
const handoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/__handoff',
  beforeLoad: sessionGate,
  component: function HandoffRouteComponent() {
    const { memberships, loading } = useAuth();
    // Wait for /auth/me to resolve before computing the landing target —
    // otherwise memberships is still [] and we would bounce to /onboarding
    // instead of the operator's hotel. Mirrors how login waits for fetchMe.
    if (loading) return <LoadingState data-testid="handoff-loading" />;
    const target = firstAccessibleHotelPath(memberships) ?? '/onboarding';
    return <Navigate to={target} replace />;
  },
});

// ---------------------------------------------------------------------------
// Hotel-scoped layout: /$tenantSlug/$hotelSlug
// ---------------------------------------------------------------------------

const hotelLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/$tenantSlug/$hotelSlug',
  beforeLoad: sessionGate,
  component: HotelGuard,
});

const hotelIndexRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: '/',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/$tenantSlug/$hotelSlug/reception', params });
  },
});

/** A guarded leaf route: permission (RBAC+ABAC) + optional feature flag. */
function guarded<const TPath extends string>(
  path: TPath,
  element: ReactNode,
  permission: Parameters<typeof PermissionRoute>[0]['permission'],
  flag?: Parameters<typeof PermissionRoute>[0]['flag'],
) {
  return createRoute({
    getParentRoute: () => hotelLayoutRoute,
    path,
    component: () => (
      <PermissionRoute permission={permission} flag={flag}>
        {element}
      </PermissionRoute>
    ),
  });
}

// --- Reception group ---
// Header/ledger actions navigate imperatively (like the calls list), so this
// route owns a component that wires the desk's callbacks to the router.
const receptionRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: 'reception',
  component: function ReceptionRouteComponent() {
    const navigate = useNavigate();
    const { canInScope } = useAuth();
    const { tenantId, hotelId } = useHotelContext();
    const { tenantSlug, hotelSlug } = useParams({ from: '/$tenantSlug/$hotelSlug' });
    const serviceRoutingFlag = useFlag('in_stay_operations');

    const scope = { tenantId, hotelId };
    const canCallHandling = canInScope('hotel:read', scope);
    const canServiceRouting = serviceRoutingFlag && canInScope('service_request:read', scope);

    return (
      <PermissionRoute permission="call:read">
        <ReceptionPage
          onViewAllCalls={() =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/calls',
              params: { tenantSlug, hotelSlug },
            })
          }
          onSelectCall={(callId) =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/calls/$callId',
              params: { tenantSlug, hotelSlug, callId },
            })
          }
          onThisWeek={() =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/analytics',
              params: { tenantSlug, hotelSlug },
            })
          }
          onNewCallHandlingRule={
            canCallHandling
              ? () =>
                  void navigate({
                    to: '/$tenantSlug/$hotelSlug/handoff-rules',
                    params: { tenantSlug, hotelSlug },
                  })
              : undefined
          }
          onNewServiceRoutingRule={
            canServiceRouting
              ? () =>
                  void navigate({
                    to: '/$tenantSlug/$hotelSlug/tasks/routing',
                    params: { tenantSlug, hotelSlug },
                  })
              : undefined
          }
        />
      </PermissionRoute>
    );
  },
});
const liveCallsRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: 'live-calls',
  component: function LiveCallsRouteComponent() {
    const navigate = useNavigate();
    const { tenantSlug, hotelSlug } = useParams({ from: '/$tenantSlug/$hotelSlug' });
    return (
      <PermissionRoute permission="call:read">
        <LiveCallsPage
          onViewCall={(callId) =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/calls/$callId',
              params: { tenantSlug, hotelSlug, callId },
            })
          }
        />
      </PermissionRoute>
    );
  },
});
const conversationsRoute = guarded('conversations', <ConversationsPage />, 'conversation:read');

// Calls list: filters/search/page live in the URL search params; a row opens
// the call detail route (client-side navigation).
const CALL_STATUSES = new Set([
  'created',
  'ringing',
  'answered',
  'ai_active',
  'handoff_requested',
  'human_active',
  'completed',
  'failed',
]);

/**
 * Parse the calls list URL search into a CallsFilters object. Only provided,
 * valid params are kept — defaults (limit 50 / offset 0) are owned by the page
 * and backend, so a bare `/calls` URL stays clean (no injected query string).
 */
function parseCallsSearch(raw: Record<string, unknown>): CallsFilters {
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const num = (v: unknown) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const status = str(raw.status);
  const direction = str(raw.direction);
  const limit = num(raw.limit);
  const offset = num(raw.offset);
  const out: CallsFilters = {};
  if (status && CALL_STATUSES.has(status)) out.status = status as CallsFilters['status'];
  if (direction === 'inbound' || direction === 'outbound') out.direction = direction;
  if (str(raw.language)) out.language = str(raw.language);
  if (str(raw.q)) out.q = str(raw.q);
  if (str(raw.from)) out.from = str(raw.from);
  if (str(raw.to)) out.to = str(raw.to);
  if (limit !== undefined) out.limit = Math.min(Math.max(limit, 1), 200);
  if (offset !== undefined && offset > 0) out.offset = offset;
  return out;
}

const callsRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: 'calls',
  validateSearch: parseCallsSearch,
  component: function CallsRouteComponent() {
    const navigate = useNavigate();
    const { tenantSlug, hotelSlug } = useParams({ from: '/$tenantSlug/$hotelSlug' });
    const search = useSearch({ from: callsRoute.id });
    return (
      <PermissionRoute permission="call:read">
        <CallsPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/calls',
              params: { tenantSlug, hotelSlug },
              search: next,
            })
          }
          onSelectCall={(callId) =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/calls/$callId',
              params: { tenantSlug, hotelSlug, callId },
            })
          }
        />
      </PermissionRoute>
    );
  },
});

// Call detail reads the $callId param; "back" returns to the list.
const callDetailRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: 'calls/$callId',
  component: function CallDetailRouteComponent() {
    const navigate = useNavigate();
    const { tenantSlug, hotelSlug, callId } = useParams({
      from: '/$tenantSlug/$hotelSlug/calls/$callId',
    });
    return (
      <PermissionRoute permission="call:read">
        <CallDetailPage
          callId={callId}
          onBack={() =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/calls',
              params: { tenantSlug, hotelSlug },
            })
          }
        />
      </PermissionRoute>
    );
  },
});

// --- Hospitality group ---
const RESERVATION_STATUSES = new Set([
  'new',
  'missing_information',
  'offer_needed',
  'offer_drafted',
  'offer_sent',
  'waiting_for_guest',
  'converted',
  'lost',
  'cancelled',
]);

/** Parse the reservations list URL search into a ReservationsFilters object. */
function parseReservationsSearch(raw: Record<string, unknown>): ReservationsFilters {
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const num = (v: unknown) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const status = str(raw.status);
  const urgency = str(raw.urgency);
  const limit = num(raw.limit);
  const offset = num(raw.offset);
  const out: ReservationsFilters = {};
  if (status && RESERVATION_STATUSES.has(status))
    out.status = status as ReservationsFilters['status'];
  if (urgency === 'low' || urgency === 'medium' || urgency === 'high') out.urgency = urgency;
  if (str(raw.contactLanguage)) out.contactLanguage = str(raw.contactLanguage);
  if (str(raw.q)) out.q = str(raw.q);
  if (str(raw.from)) out.from = str(raw.from);
  if (str(raw.to)) out.to = str(raw.to);
  if (limit !== undefined) out.limit = Math.min(Math.max(limit, 1), 200);
  if (offset !== undefined && offset > 0) out.offset = offset;
  return out;
}

const reservationsRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: 'reservations',
  validateSearch: parseReservationsSearch,
  component: function ReservationsRouteComponent() {
    const navigate = useNavigate();
    const { tenantSlug, hotelSlug } = useParams({ from: '/$tenantSlug/$hotelSlug' });
    const search = useSearch({ from: reservationsRoute.id });
    return (
      <PermissionRoute permission="reservation:read">
        <ReservationsPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/reservations',
              params: { tenantSlug, hotelSlug },
              search: next,
            })
          }
        />
      </PermissionRoute>
    );
  },
});
const BOOKING_STATUSES = new Set([
  'requested',
  'draft',
  'pending_staff_approval',
  'confirmed',
  'cancelled',
  'failed',
  'expired',
]);

/** Parse the bookings list URL search into a BookingsFilters object. */
function parseBookingsSearch(raw: Record<string, unknown>): BookingsFilters {
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const num = (v: unknown) => {
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
    return Number.isFinite(n) ? n : undefined;
  };
  const status = str(raw.status);
  const mode = str(raw.confirmationMode);
  const limit = num(raw.limit);
  const offset = num(raw.offset);
  const out: BookingsFilters = {};
  if (status && BOOKING_STATUSES.has(status)) out.status = status as BookingsFilters['status'];
  if (mode === 'manual' || mode === 'pms') out.confirmationMode = mode;
  if (str(raw.q)) out.q = str(raw.q);
  if (str(raw.from)) out.from = str(raw.from);
  if (str(raw.to)) out.to = str(raw.to);
  if (limit !== undefined) out.limit = Math.min(Math.max(limit, 1), 200);
  if (offset !== undefined && offset > 0) out.offset = offset;
  return out;
}

const bookingsRoute = createRoute({
  getParentRoute: () => hotelLayoutRoute,
  path: 'bookings',
  validateSearch: parseBookingsSearch,
  component: function BookingsRouteComponent() {
    const navigate = useNavigate();
    const { tenantSlug, hotelSlug } = useParams({ from: '/$tenantSlug/$hotelSlug' });
    const search = useSearch({ from: bookingsRoute.id });
    return (
      <PermissionRoute permission="booking:read">
        <BookingsPage
          filters={search}
          onFiltersChange={(next) =>
            void navigate({
              to: '/$tenantSlug/$hotelSlug/bookings',
              params: { tenantSlug, hotelSlug },
              search: next,
            })
          }
        />
      </PermissionRoute>
    );
  },
});
const tasksRoute = guarded('tasks', <TasksPage />, 'service_request:read', 'in_stay_operations');
const tasksRoutingRoute = guarded(
  'tasks/routing',
  <TasksRoutingPage />,
  'service_request:read',
  'in_stay_operations',
);

// --- Intelligence group ---
const knowledgeRoute = guarded(
  'knowledge',
  <KnowledgePage />,
  'knowledge:read',
  'knowledge_onboarding',
);
const onboardingHotelRoute = guarded(
  'onboarding',
  <OnboardingReviewPage />,
  'onboarding:read',
  'knowledge_onboarding',
);
const analyticsRoute = guarded('analytics', <AnalyticsPage />, 'call:read');
const usageRoute = guarded('usage', <UsagePage />, 'usage:read', 'usage_reporting');

// --- Settings group ---
const hotelSetupRoute = guarded('hotel-setup', <HotelSetupPage />, 'hotel:read');
const hotelProfileRoute = guarded('hotel-profile', <HotelProfilePage />, 'hotel:read');
const scriptsRoute = guarded('scripts', <ScriptsPage />, 'hotel:read');
const handoffRulesRoute = guarded('handoff-rules', <HandoffRulesPage />, 'hotel:read');
const integrationsRoute = guarded(
  'integrations',
  <IntegrationsPage />,
  'integration:read',
  'integrations_management',
);
const teamRoute = guarded('team', <TeamPage />, 'user:read');
const settingsRoute = guarded('settings', <SettingsPage />, 'hotel:read');

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  onboardingRoute,
  handoffRoute,
  hotelLayoutRoute.addChildren([
    hotelIndexRoute,
    receptionRoute,
    liveCallsRoute,
    callsRoute,
    callDetailRoute,
    conversationsRoute,
    reservationsRoute,
    bookingsRoute,
    tasksRoute,
    tasksRoutingRoute,
    knowledgeRoute,
    onboardingHotelRoute,
    analyticsRoute,
    usageRoute,
    hotelSetupRoute,
    hotelProfileRoute,
    scriptsRoute,
    handoffRulesRoute,
    integrationsRoute,
    teamRoute,
    settingsRoute,
  ]),
]);

export function createAppRouter(context: RouterContext) {
  return createRouter({
    routeTree,
    context,
    defaultPreload: 'intent',
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

// `useNavigate` is re-exported for feature pages that need imperative navigation
// (e.g. a call row opening the call detail route) without importing the router.
export { useNavigate };
